import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { StyledCard } from '@app/components/card/Card';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import { StyledTag } from '@app/components/tag/Tag';
import useInteractionList from '@app/hooks/interactions/useInteractions';
import useInteraction from '@app/hooks/interactions/useInteraction';
import useInterationsActions from '@app/hooks/interactions/useInterationsActions';
import useDashboardStats from '@app/hooks/dashboard/useDashboardStats';
import type { Interaction, InteractionListParams } from '@app/services/api.types';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import { Flex, type TableColumnsType } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { CgSoftwareDownload } from 'react-icons/cg';
import { Link, useSearchParams } from 'react-router';
import InteractionsLineChart from '@app/components/interactions-line-chart/InteractionsLineChart';
import { InteractionDrawer } from './components/InteractionDrawer';
import InteractionFilters from './components/InteractionFilters';
import dayjs from 'dayjs';
import { DateRangeSelect } from '@app/components/date-range-select/DateRangeSelect';
import InteractionTypeTag from './components/InteractionTypeTag';

export const InteractionsList = () => {
    const [searchParams] = useSearchParams();
    const { exportInteractions } = useInterationsActions();
    const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
    const lastRangeRef = React.useRef<{ start?: string; end?: string }>({});

    const [params, setParams] = useState<InteractionListParams>({
        page: 1,
        per_page: 10,
        search: '',
        type_id: [],
        ai_agent_type_id: [],
        brand_id: [],
        location_id: [],
        contact_id: undefined,
        order_by: undefined,
        order_dir: undefined,
        start: undefined,
        end: undefined,
    });

    useEffect(() => {
        const contactId = searchParams.get('contact_id');
        setParams((prev) => ({
            ...prev,
            contact_id: contactId || undefined,
        }));
    }, [searchParams]);

    // Fetch data from the API
    const { isPending, data: interactionList } = useInteractionList(params);
    const { data: selectedInteraction } = useInteraction(
        { id: selectedRecordId },
        {
            enabled: Boolean(selectedRecordId),
        }
    );

    const [statsParams, setStatsParams] = useState({
        start: '',
        end: '',
    });

    const { useDailyStats } = useDashboardStats();
    const { data: statsData, isLoading: statsLoading } = useDailyStats(statsParams, {
        staleTime: 1000 * 60 * 5,
    });

    useEffect(() => {
        if (!statsParams.start || !statsParams.end) return;

        const last = lastRangeRef.current;
        if (last.start === statsParams.start && last.end === statsParams.end) return;

        lastRangeRef.current = { start: statsParams.start, end: statsParams.end };

        setParams((prev) => ({
            ...prev,
            start: statsParams.start,
            end: statsParams.end,
            page: 1,
        }));
    }, [statsParams.start, statsParams.end]);

    const columns: TableColumnsType<Interaction> = [
        {
            title: 'Timestamp',
            dataIndex: 'start',
            sorter: true,
            render: (value) => {
                return value ? fn.formatDate(value, 'DD/MM/YYYY, HH:mm') : '-';
            },
        },
        {
            title: 'Type',
            dataIndex: 'type_name',
            key: 'type_name',
            sorter: true,
            render: (value) => {
                return <InteractionTypeTag interaction={value} />;
            },
        },
        {
            title: 'AI Agent Type',
            dataIndex: 'ai_agent_type_name',
            key: 'ai_agent_type_name',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Brand',
            dataIndex: 'brand_name',
            key: 'brand_name',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Office',
            dataIndex: 'location_name',
            key: 'location_name',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Contact',
            dataIndex: 'contact',
            key: 'contact',
            sorter: false,
            render: (_, record) => {
                return (
                    <div>
                        <span>
                            {[record.contact_first_name, record.contact_last_name]
                                .filter(Boolean)
                                .join(' ') || '-'}
                        </span>
                        <br />
                        <small>{record.contact_number}</small>
                    </div>
                );
            },
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            sorter: true,
            render: (value) => (value ? fn.secondsToMinutesSeconds(value) : 'N/A'),
        },
        {
            title: 'Sales Viewing',
            dataIndex: 'sales_viewing',
            key: 'sales_viewing',
            sorter: true,
            render: (salesViewing) => {
                return (
                    <StyledTag color={salesViewing ? 'green' : 'default'}>
                        {salesViewing ? 'Yes' : 'No'}
                    </StyledTag>
                );
            },
            width: 140,
        },
        {
            title: 'Lettings Viewing',
            dataIndex: 'lettings_viewing',
            key: 'lettings_viewing',
            sorter: true,
            render: (lettingsViewing) => {
                return (
                    <StyledTag color={lettingsViewing ? 'green' : 'default'}>
                        {lettingsViewing ? 'Yes' : 'No'}
                    </StyledTag>
                );
            },
            width: 140,
        },
        {
            title: 'Valuation',
            dataIndex: 'valuation_booked',
            key: 'valuation_booked',
            sorter: true,
            render: (valuation) => {
                return (
                    <StyledTag color={valuation ? 'green' : 'default'}>
                        {valuation ? 'Yes' : 'No'}
                    </StyledTag>
                );
            },
            width: 140,
        },
        {
            title: 'FS Booking',
            dataIndex: 'fs_booking',
            key: 'fs_booking',
            sorter: true,
            render: (fsBooking) => {
                return (
                    <StyledTag color={fsBooking ? 'green' : 'default'}>
                        {fsBooking ? 'Yes' : 'No'}
                    </StyledTag>
                );
            },
            width: 140,
        },
        {
            title: 'Sell Opportunity',
            dataIndex: 'sell_opportunity',
            key: 'sell_opportunity',
            sorter: true,
            render: (sellOpportunity) => {
                return (
                    <StyledTag color={sellOpportunity ? 'green' : 'default'}>
                        {sellOpportunity ? 'Yes' : 'No'}
                    </StyledTag>
                );
            },
            width: 140,
        },
        {
            title: 'Mortgage Opportunity',
            dataIndex: 'mortgage_opportunity',
            key: 'mortgage_opportunity',
            sorter: true,
            render: (mortgageOpportunity) => {
                return (
                    <StyledTag color={mortgageOpportunity ? 'green' : 'default'}>
                        {mortgageOpportunity ? 'Yes' : 'No'}
                    </StyledTag>
                );
            },
            width: 140,
        },
        {
            title: 'Call Forwarded',
            dataIndex: 'forwarded_to',
            key: 'forwarded_to',
            sorter: true,
            render: (value, record) => {
                return record.type_name === 'Call' ? (
                    <StyledTag color={value ? 'green' : 'default'}>
                        {value ? 'Yes' : 'No'}
                    </StyledTag>
                ) : (
                    <div className="tag-placeholder">-</div>
                );
            },
            width: 140,
        },
    ];

    const getDifferenceInDays = (date1: string, date2: string) => {
        if (!date1 || !date2) {
            return 0;
        }

        const start = dayjs(date1);
        const end = dayjs(date2);
        const differenceInDays = end.diff(start, 'days') + 1; // offset 1 day
        return differenceInDays;
    };

    const timeSeriesData = useMemo(() => {
        const differenceInDays = getDifferenceInDays(statsParams.start, statsParams.end);
        const startDate = statsParams.end ? new Date(statsParams.end).getTime() : Date.now();
        const items = Array.isArray(statsData) ? statsData : [];
        return fn.generateTimeSeriesData(differenceInDays, items, startDate);
    }, [statsParams, statsData]);

    const handleExport = () => {
        exportInteractions.mutate(params);
    };

    const tableKey = useMemo(() => JSON.stringify(params), [params]);

    return (
        <React.Fragment>
            <PageMeta title="Interactions" />

            <PageHeader
                breadcrumbItems={[
                    {
                        title: <Link to={url.adminPortal.dashboard}>Dashboard</Link>,
                    },
                    { title: 'Interactions' },
                ]}>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="Interactions" />
                        <PageIntro>
                            <p>View and manage all customer interactions</p>
                        </PageIntro>
                        {/* <div
                            style={{
                                marginTop: 6,
                                color: 'var(--muted-color)',
                            }}
                        >
                            <small>
                                Showing: <strong>{startDate}</strong> —{' '}
                                <strong>{endDate}</strong>
                            </small>
                        </div> */}
                    </div>

                    <DateRangeSelect
                        disabled={statsLoading || isPending}
                        onSelectionChange={(value) => {
                            setStatsParams(value);
                        }}
                    />
                </Flex>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content>
                    <StyledCard title="Interactions Over Time">
                        <InteractionsLineChart
                            data={timeSeriesData}
                            series={[
                                {
                                    key: 'total',
                                    name: 'Total Interactions',
                                    color: '#3b82f6',
                                },
                            ]}
                        />
                    </StyledCard>

                    <br />

                    <Flex align="end" className="styled-table-header">
                        <InteractionFilters
                            onChange={(values) => {
                                setParams((prev) => ({ ...prev, ...values, page: 1 }));
                            }}
                        />
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    icon={<CgSoftwareDownload size={20} />}
                                    onClick={handleExport}>
                                    Export CSV
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    </Flex>

                    <br />

                    <StyledTable<Interaction>
                        key={tableKey}
                        columns={columns}
                        dataSource={interactionList?.data || []}
                        rowKey={({ id }) => id}
                        loading={isPending}
                        pagination={{
                            current: params.page,
                            pageSize: params.per_page,
                            total: interactionList?.total || 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order_dir = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                per_page: pagination.pageSize,
                                order_by,
                                order_dir,
                            }));
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => setSelectedRecordId(record.id),
                            };
                        }}
                        rowClassName={() => 'is-clickable'}
                    />

                    <InteractionDrawer
                        size={700}
                        onClose={() => setSelectedRecordId(null)}
                        open={Boolean(selectedRecordId)}
                        data={selectedInteraction?.data ?? null}
                    />
                </PageLayout.Content>
            </PageLayout>
        </React.Fragment>
    );
};

export default InteractionsList;
